import { Button, Form, Input, Flex, notification, Modal, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { httpService } from "../../../base/services/httpService.service";
import { SessionService } from "../../../base/services/session.service";
import "../style.scss";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { finishAwaitingConfirmation, openSignUpSession } from "./registrationSession";
import {
   codeTimeLeft,
   formatCodeTimeLeft,
   isCodeExpired,
   isCodeStillPending
} from "./confirmationCode";

// The code that proves the address an account was registered with. It is opened
// from the sign up flow, which has just created the account (`codeSentAt`), and
// from the login page, which turns an unconfirmed address away and has a fresh
// code mailed as the modal opens (`sendCodeOnOpen`).
//
// A code is good for five minutes, and the server sends no other while one is
// (confirmationCode.js). So a code known to have gone out is counted down, and
// a new one is offered once its time is up — or at once, when the code typed
// in turns out to have run out already.
//
// Every way out of it says something: a code that is refused, a security check
// that has not loaded, a code that never arrived. The one thing it must never
// do is sit there having apparently done nothing, because there is no other
// screen to go and finish this on.

function ConfirmationCodeModal(props) {
   const [form] = Form.useForm();
   const navigate = useNavigate();
   const [isLoading, setIsLoading] = useState(false);
   const [isResending, setIsResending] = useState(false);
   const [api, contextHolder] = notification.useNotification();
   const { executeRecaptcha } = useGoogleReCaptcha();
   // The last code known to be out: to which address, when, and whether it is
   // a new one ("sent") or one sent earlier that the server kept ("pending",
   // sent at most five minutes before `at`). Null when nothing is known, as
   // after a reload.
   const [lastSend, setLastSend] = useState(null);
   // The address whose code confirmEmail said had run out.
   const [expiredFor, setExpiredFor] = useState(null);
   const [now, setNow] = useState(() => Date.now());

   // The login page opens this one modal for whichever account signs in, so
   // what is known about one address says nothing about another's.
   const knownSend = lastSend?.email === props.email ? lastSend : null;
   const isExpired = Boolean(props.email) && expiredFor === props.email;
   const timeLeft = isExpired ? 0 : codeTimeLeft(knownSend?.at, now);
   const isWaiting = timeLeft > 0;

   const startWaiting = (kind, at = Date.now()) => {
      setNow(at);
      setLastSend({ email: props.email, at, kind });
      setExpiredFor(null);
   };

   // The sign up has just had a code mailed by creating the account.
   useEffect(() => {
      if (props.codeSentAt) startWaiting("sent", props.codeSentAt);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.codeSentAt]);

   useEffect(() => {
      if (!isWaiting) return undefined;
      const timer = setInterval(() => setNow(Date.now()), 1000);
      return () => clearInterval(timer);
   }, [isWaiting]);

   const report = (type, message, description) => api[type]({ message, description });

   // Whatever the call refused for, read off whichever shape the failure has.
   // A refusal that came back on a 200 has already been shown by the response
   // interceptor, so it is not said twice.
   const reportFailure = (error, fallback) => {
      if (error?.isSuccess === false) return;

      const failure =
         error?.response?.data?.errors?.[0] ??
         error?.response?.data?.message ??
         error?.errors?.[0] ??
         error?.message;

      report("error", "Error", failure ?? fallback);
   };

   const verifyCode = async () => {
      if (isLoading) return;

      // Said out loud rather than returned from silently: this is the only
      // button on a modal that cannot be dismissed past.
      if (!executeRecaptcha) {
         report(
            "warning",
            "Warning",
            "The security check is still loading. Please try again in a moment."
         );
         return;
      }

      // The field's own rules, actually run. Without this the six digits it
      // asks for were never checked and a half-typed code was sent anyway.
      let codeValue;
      try {
         ({ codeValue } = await form.validateFields());
      } catch (invalid) {
         return;
      }

      const userId = SessionService.getItem("currentUserId");
      if (!userId) {
         report(
            "error",
            "Error",
            "We've lost track of which account this code belongs to. Please log in to continue."
         );
         return;
      }

      setIsLoading(true);

      const recaptchaToken = await executeRecaptcha("login");

      await httpService
         .post(
            "/Account/confirmEmail",
            {
               userId: userId,
               code: codeValue
            },
            async (res) => {
               finishAwaitingConfirmation();

               // With the new billing flow on, a confirmed email is answered
               // with a token that only says who this is. The billing calls
               // that finish the sign up need the organization's, which is
               // asked for with it (registrationSession.js). Nothing comes
               // back, and nothing is asked for, with the flow off. Nor on the
               // login page, which keeps no organization here: the sign in it
               // makes next opens the session itself.
               const organizationId = SessionService.getItem("currentOrgId");
               if (res?.data?.token && organizationId) {
                  const opened = await openSignUpSession(res.data.token, { userId, organizationId });

                  // The address is proven, so signing in picks the sign up up
                  // at its package step, and asks for the session again.
                  if (!opened) {
                     setIsLoading(false);
                     props.handleClose();
                     notification.error({
                        message: "Oops! A problem has occurred!",
                        description:
                           "Your email is confirmed, but we couldn't open your account. Please log in to continue."
                     });
                     navigate("/auth/login");
                     return;
                  }
               }

               setIsLoading(false);
               props.confirmCode();
               props.handleClose();
            },
            (error) => {
               setIsLoading(false);

               // Said under the field, next to the way to get a new one, which
               // the server now sends at once.
               if (isCodeExpired(error)) {
                  setExpiredFor(props.email);
                  return;
               }

               reportFailure(error, "That code didn't match. Please check it and try again.");
            },
            undefined,
            {
               "g-recaptcha-response": recaptchaToken
            }
         )
         .catch(() => {
            // Already reported in the callback above; the rejection is not news.
         });
   };

   // The code that never arrived. Without a way to ask for another one, an
   // email that went astray ends the registration here.
   const resendCode = async () => {
      if (isResending || !props?.email) return;

      if (!executeRecaptcha) {
         report(
            "warning",
            "Warning",
            "The security check is still loading. Please try again in a moment."
         );
         return;
      }

      setIsResending(true);

      const recaptchaToken = await executeRecaptcha("login");

      await httpService
         .post(
            `/Account/resendConfirmEmail?email=${encodeURIComponent(props.email)}`,
            {},
            () => {
               setIsResending(false);
               form.resetFields(["codeValue"]);
               startWaiting("sent");
               report("success", "New code sent", `A new code was sent to ${props.email}.`);
            },
            (error) => {
               setIsResending(false);

               // Nothing wrong: the code sent a moment ago is still good, and
               // a new one can be asked for once it has run out.
               if (isCodeStillPending(error)) {
                  startWaiting("pending");
                  report(
                     "info",
                     "Check your inbox",
                     "We already sent you a code a moment ago. Check your inbox, including spam."
                  );
                  return;
               }

               reportFailure(error, "We couldn't send a new code. Please try again.");
            },
            undefined,
            {
               "g-recaptcha-response": recaptchaToken
            }
         )
         .catch(() => {});
   };

   // The login page's modal asks for a code as it opens. One mailed at sign up
   // that has since run out is replaced, so somebody coming back later has a
   // fresh code waiting; one still valid is kept, and they are told so.
   useEffect(() => {
      if (props.isModaOpen && props.sendCodeOnOpen && !isWaiting) resendCode();
      // Once per opening, not again as the countdown ticks.
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [props.isModaOpen]);

   return (
      <div className="conf-modal-wrapper">
         {contextHolder}
         <Modal
            style={{ width: "450px", top: "calc(50% - 230px)" }}
            open={props?.isModaOpen}
            closable={false}
            mask={true}
            maskClosable={false}
            getContainer={document.querySelector(".conf-modal-wrapper")}
         >
            <Form form={form}>
               <div className="conf-inner">
                  <h1>Verification code</h1>
                  <p>
                     Please confirm your email address to proceed with the payment. Check your inbox
                     for the verification code, and once confirmed, you can seamlessly move on to
                     the next step. Thank you!
                  </p>
               </div>
               <div className="code-input">
                  <Form.Item
                     name="codeValue"
                     rules={[
                        {
                           required: true,
                           message: "Please enter the code from your email"
                        },
                        {
                           pattern: /^\d{6}$/,
                           message: "The code is 6 digits"
                        }
                     ]}
                  >
                     <Input
                        maxLength={6}
                        style={{
                           maxWidth: "210px",
                           height: "70px",
                           fontSize: "50px",
                           fontWeight: "bold",
                           color: "#003DAA",
                           textAlign: "center"
                        }}
                        autoComplete="off"
                        onPressEnter={verifyCode}
                     />
                  </Form.Item>
               </div>
               {props?.email && (
                  <p style={{ textAlign: "center" }}>
                     {isWaiting ? (
                        knownSend.kind === "sent" ? (
                           `This code is valid for ${formatCodeTimeLeft(timeLeft)}.`
                        ) : (
                           `You can ask for a new code in ${formatCodeTimeLeft(timeLeft)}.`
                        )
                     ) : (
                        <>
                           {isExpired ? (
                              <Typography.Text type="danger">This code has expired.</Typography.Text>
                           ) : knownSend ? (
                              "This code has expired."
                           ) : (
                              "Didn’t get it?"
                           )}{" "}
                           <Button
                              type="link"
                              style={{ padding: 0 }}
                              loading={isResending}
                              onClick={resendCode}
                           >
                              Send a new code
                           </Button>
                        </>
                     )}
                  </p>
               )}
               <div className="button-split">
                  <Flex
                     gap="medium"
                     wrap="wrap"
                     style={{ justifyContent: "space-evenly", marginTop: "40px" }}
                  >
                     <Button
                        style={{
                           color: "#4158CF",
                           borderColor: "#4158CF",
                           height: "auto",
                           padding: "10px 15px"
                        }}
                        size="large"
                        onClick={props?.handleClose}
                     >
                        Cancel
                     </Button>
                     <Button
                        style={{
                           backgroundColor: "#4158CF",
                           height: "auto",
                           padding: "10px 15px"
                        }}
                        type="primary"
                        size="large"
                        loading={isLoading}
                        onClick={verifyCode}
                     >
                        Verify code
                     </Button>
                  </Flex>
               </div>
            </Form>
         </Modal>
      </div>
   );
}

export default ConfirmationCodeModal;
